import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiAlertTriangle, FiArrowRightCircle, FiCamera, FiCheck, FiCheckCircle, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import styles from './AssessmentPage.module.css';
import ProctoringViolationTracker from '../../utils/proctoringTracker';

// Skeleton loaders for different question types
function QuestionSkeleton() {
  return (
    <div className={styles.questionCard}>
      <div className={styles.skeletonQuestionText} />
      <div className={styles.skeletonOptions}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className={styles.skeletonOption} />
        ))}
      </div>
    </div>
  );
}

function LikertSkeleton() {
  return (
    <div className={styles.questionCard}>
      <div className={styles.skeletonQuestionText} />
      <div className={styles.skeletonLikert} />
    </div>
  );
}

function OpenSkeleton() {
  return (
    <div className={styles.questionCard}>
      <div className={styles.skeletonQuestionText} />
      <div className={styles.skeletonTextarea} />
    </div>
  );
}

const SECTIONS = [
  { key: 'cognitive', label: 'Cognitive', badgeLabel: 'Cognitive, Logical & Numerical Reasoning', shortLabel: 'Cognitive & Analytical Skills' },
  { key: 'personality', label: 'Personality & EQ', badgeLabel: 'Personality & Emotional Intelligence', shortLabel: 'Personality & Emotional Intelligence' },
  { key: 'leadership', label: 'Leadership', badgeLabel: 'Leadership & Team Management', shortLabel: 'Leadership & Team Management' },
  { key: 'casestudy', label: 'Case Study', badgeLabel: 'Technical Case Study Analysis', shortLabel: 'Technical Case Study' },
];

const TOTAL_TIME = 6300; // 1 hour 45 minutes in seconds

export default function AssessmentPage() {
  const { candidate, candidateLogout } = useAuth();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('cognitive');
  const [questions, setQuestions] = useState({ cognitive: [], personality: [], leadership: [], casestudy: [] });
  const [answers, setAnswers] = useState({ cognitive: {}, personality: {}, leadership: {}, casestudy: {} });
  const [currentQ, setCurrentQ] = useState({ cognitive: 0, personality: 0, leadership: 0, casestudy: -1 });
  const [viewingCaseStudyScenario, setViewingCaseStudyScenario] = useState(false);
  const [loadingQ, setLoadingQ] = useState(false);
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [accessReady, setAccessReady] = useState(false);
  const [assessmentPaused, setAssessmentPaused] = useState(false);
  const [accountDeactivated, setAccountDeactivated] = useState(false);
  const [accessError, setAccessError] = useState(false);
  const [timeExpired, setTimeExpired] = useState(false); // Track if time has expired
  const [skipConfirmModal, setSkipConfirmModal] = useState(false); // Skip confirmation when time expired
  const [submitting, setSubmitting] = useState(false);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(5);
  const [sectionTransition, setSectionTransition] = useState(null); // {from, to}
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [validationError, setValidationError] = useState(null); // {title, message, unansweredCount}
  const [sectionConfirmModal, setSectionConfirmModal] = useState(null); // section key for confirmation
  const [sectionSubmitInfo, setSectionSubmitInfo] = useState(null); // {section, unansweredCount}
  const [finalSubmitWarning, setFinalSubmitWarning] = useState(null); // {unansweredBySection, totalUnanswered}
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraPos, setCameraPos] = useState({ x: 0, y: 155 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const timerRef = useRef(null);
  const lastSaved = useRef(0);
  const videoRef = useRef(null);
  const cameraCardRef = useRef(null);
  const cameraStreamRef = useRef(null); // Persistent reference to camera stream
  const accessStateRef = useRef({ assessmentPaused: false, deactivated: false });
  const serverTimeIsAuthoritative = useRef(false);
  const initializedCandidateRef = useRef(null);

  // Reset state when candidate changes to prevent data leakage
  useEffect(() => {
    setQuestions({ cognitive: [], personality: [], leadership: [], casestudy: [] });
    setAnswers({ cognitive: {}, personality: {}, leadership: {}, casestudy: {} });
    setCurrentQ({ cognitive: 0, personality: 0, leadership: 0, casestudy: -1 });
    setActiveSection('cognitive');
    initializedCandidateRef.current = null;
    accessStateRef.current = { assessmentPaused: false, deactivated: false };
    serverTimeIsAuthoritative.current = false;
    setAccessReady(false);
    setAssessmentPaused(false);
    setAccountDeactivated(false);
    setAccessError(false);
  }, [candidate?.id]);

  const checkAssessmentAccess = useCallback(async () => {
    try {
      const res = await API.get('/candidates/assessment-access');
      const nextState = {
        assessmentPaused: res.data.assessmentPaused,
        deactivated: res.data.deactivated
      };
      const previousState = accessStateRef.current;
      if (
        nextState.assessmentPaused ||
        nextState.deactivated ||
        previousState.assessmentPaused ||
        previousState.deactivated
      ) {
        setTimeLeft(res.data.timeRemaining);
        serverTimeIsAuthoritative.current = true;
      }
      accessStateRef.current = nextState;
      setAssessmentPaused(nextState.assessmentPaused);
      setAccountDeactivated(nextState.deactivated);
      setAccessError(false);
      setAccessReady(true);
    } catch (error) {
      console.error('Failed to verify assessment access:', error);
      setAccessError(true);
      setAccessReady(true);
    }
  }, []);

  useEffect(() => {
    if (!candidate) return undefined;
    checkAssessmentAccess();
    const interval = setInterval(checkAssessmentAccess, 2000);
    return () => clearInterval(interval);
  }, [candidate, checkAssessmentAccess]);

  // Prevent browser back button from working
  useEffect(() => {
    // Push a dummy state to the history stack
    window.history.pushState(null, null, window.location.href);

    const handlePopState = () => {
      // Push again to prevent actual back navigation
      window.history.pushState(null, null, window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Reset countdown when submitted
  useEffect(() => {
    if (submitted) {
      setCountdownSeconds(5);
      // Persist submitted state to localStorage
      if (candidate) {
        localStorage.setItem(`assessmentSubmitted_${candidate.id}`, 'true');
      }
    }
  }, [submitted, candidate]);

  // Auto sign out after 5 seconds when assessment is submitted
  useEffect(() => {
    if (submitted) {
      if (countdownSeconds <= 0) {
        // Clear submitted flag from localStorage
        if (candidate) {
          localStorage.removeItem(`assessmentSubmitted_${candidate.id}`);
        }
        candidateLogout();
        navigate('/login');
        return;
      }
      const timer = setInterval(() => {
        setCountdownSeconds(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [submitted, countdownSeconds, candidateLogout, navigate]);

  // Lazy load questions: only load cognitive initially, then load others on-demand
  useEffect(() => {
    if (!candidate) {
      navigate('/login');
      return;
    }
    if (!accessReady || assessmentPaused || accountDeactivated || accessError) return;
    if (initializedCandidateRef.current === candidate.id) return;
    initializedCandidateRef.current = candidate.id;
    
    // Check if assessment was already submitted (persisted in localStorage)
    const submittedKey = `assessmentSubmitted_${candidate.id}`;
    if (localStorage.getItem(submittedKey)) {
      setSubmitted(true);
      return;
    }
    
    // Load saved progress for this candidate
    const candidateAnswersKey = `assessmentAnswers_${candidate.id}`;
    const candidateCurrentQKey = `assessmentCurrentQ_${candidate.id}`;
    const candidateTimeKey = `assessmentTime_${candidate.id}`;
    
    const savedAnswers = localStorage.getItem(candidateAnswersKey);
    const savedCurrentQ = localStorage.getItem(candidateCurrentQKey);
    const savedTime = localStorage.getItem(candidateTimeKey);
    
    if (savedAnswers) {
      setAnswers(JSON.parse(savedAnswers));
    }
    if (savedCurrentQ) {
      setCurrentQ(JSON.parse(savedCurrentQ));
    }
    // Restore time from localStorage first (persists across server restarts)
    if (savedTime && !serverTimeIsAuthoritative.current) {
      setTimeLeft(parseInt(savedTime));
    }
    
    const initAndLoad = async () => {
      // If time wasn't in localStorage, fetch from server
      if (!savedTime && !serverTimeIsAuthoritative.current) {
        try {
          const res = await API.get('/candidates/profile');
          if (res.data.timeRemaining) {
            setTimeLeft(res.data.timeRemaining);
          }
        } catch (e) { }
      }
      
      // Ensure questions exist
      await API.get('/questions/').catch(() => {});
      // Start assessment
      await API.post('/candidates/start').catch(() => {});
      
      // Load ONLY the first section initially for faster page load
      setLoadingQ(true);
      try {
        const cogRes = await API.get('/questions/cognitive');
        setQuestions(prev => ({ ...prev, cognitive: cogRes.data }));
        // Preload personality in background while user works through cognitive
        API.get('/questions/personality').then(res => {
          setQuestions(prev => ({ ...prev, personality: res.data }));
        }).catch(() => {});
      } catch (e) { }
      finally { setLoadingQ(false); }
    };
    initAndLoad();
    // eslint-disable-next-line
  }, [candidate, accessReady, assessmentPaused, accountDeactivated, accessError]);

  // Block reload and back button completely
  useEffect(() => {
    // Initialize proctoring violation tracker
    if (candidate) {
      const trackerRef = new ProctoringViolationTracker(candidate.id, new Date());
      // Store it globally so it persists
      window.proctoringTracker = trackerRef;
    }

    // Block reload
    window.onbeforeunload = () => false;

    // Block back button - continuously push state
    const pushState = () => {
      window.history.pushState(null, null, window.location.pathname);
    };
    pushState();
    window.addEventListener('popstate', pushState);

    // Block copy, cut, and paste
    const blockCopy = (e) => e.preventDefault();
    document.addEventListener('copy', blockCopy);
    document.addEventListener('cut', blockCopy);
    document.addEventListener('paste', blockCopy);

    return () => {
      window.onbeforeunload = null;
      window.removeEventListener('popstate', pushState);
      document.removeEventListener('copy', blockCopy);
      document.removeEventListener('cut', blockCopy);
      document.removeEventListener('paste', blockCopy);
    };
  }, [candidate]);

  // Setup camera feed for proctoring (only once, not on re-renders or section changes)
  useEffect(() => {
    const setupCamera = async () => {
      try {
        // Request both video and audio - audio needed for speech detection in proctoring
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        cameraStreamRef.current = stream; // Store in ref
        setCameraStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (e) {
      }
    };
    
    setupCamera();

    return () => {
      // Clean up only on unmount
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, []); // Empty dependency - only run on mount/unmount

  // Ensure video element maintains the stream even after re-renders
  useEffect(() => {
    if (videoRef.current && cameraStreamRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [activeSection]); // Re-attach stream if it gets lost on section change

  // Initialize audio detection with camera stream once it's available
  useEffect(() => {
    if (cameraStreamRef.current && window.proctoringTracker) {
      // Get audio track from camera stream for audio detection
      const audioTracks = cameraStreamRef.current.getAudioTracks();
      if (audioTracks.length > 0) {
        window.proctoringTracker.initializeAudioDetectionWithStream(cameraStreamRef.current);
      }
    }
  }, [cameraStream]);

  // Capture candidate photo at assessment start
  useEffect(() => {
    const capturePhoto = async () => {
      if (!videoRef.current || !cameraStreamRef.current || !candidate) return;
      
      try {
        // Wait for video to be ready and has enough data
        if (videoRef.current.readyState >= 2 && videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
          // Create canvas with medium dimensions (640x480 for ~200-300KB)
          const canvas = document.createElement('canvas');
          canvas.width = 640;
          canvas.height = 480;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(videoRef.current, 0, 0, 640, 480);
          
          // Convert to base64 JPEG with moderate quality (0.55) to get 200-300KB
          const photoBase64 = canvas.toDataURL('image/jpeg', 0.55);
          
          // Verify we got a valid base64 string
          if (!photoBase64 || photoBase64.length < 100) {
            console.warn('Photo capture failed: invalid base64 data');
            return;
          }
          
          // Send to backend with retry logic
          try {
            await API.put('/candidates/save-photo', { photo: photoBase64 });
            console.log(`Photo saved successfully (${photoBase64.length} bytes)`);
          } catch (error) {
            console.warn('Failed to save photo on first try, retrying...', error.message);
            // Retry once after 2 seconds
            setTimeout(async () => {
              try {
                await API.put('/candidates/save-photo', { photo: photoBase64 });
                console.log('Photo saved successfully on retry');
              } catch (e) {
                console.error('Photo save failed on retry:', e.message);
              }
            }, 2000);
          }
        } else {
          console.warn('Video not ready for photo capture');
        }
      } catch (e) {
        console.warn('Failed to capture photo:', e.message);
      }
    };

    // Capture photo 10 seconds after camera stream is ready
    if (cameraStream && videoRef.current) {
      const timer = setTimeout(capturePhoto, 10000);
      return () => clearTimeout(timer);
    }
  }, [cameraStream, candidate]);

  const loadSection = async (sec) => {
    if (questions[sec].length > 0) return;
    setLoadingQ(true);
    try {
      const res = await API.get(`/questions/${sec}`);
      setQuestions(p => ({ ...p, [sec]: res.data }));
    } catch (e) { console.error(e); }
    finally { setLoadingQ(false); }
  };

  // Preload section on tab change + preload next section in background
  const switchSection = (sec) => {
    if (timeExpired) return; // Prevent switching sections when time has expired
    setActiveSection(sec);
    loadSection(sec);
    
    // Preload next section in background
    const sectionOrder = ['cognitive', 'personality', 'leadership', 'casestudy'];
    const nextIdx = sectionOrder.indexOf(sec) + 1;
    if (nextIdx < sectionOrder.length) {
      const nextSec = sectionOrder[nextIdx];
      if (questions[nextSec].length === 0) {
        API.get(`/questions/${nextSec}`).then(res => {
          setQuestions(p => ({ ...p, [nextSec]: res.data }));
        }).catch(() => {});
      }
    }
  };

  // Timer
  useEffect(() => {
    if (!accessReady || assessmentPaused || accountDeactivated || accessError || submitted) return undefined;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        const next = prev - 1;
        // Keep the server's remaining time close to the local timer so a pause freezes it accurately.
        if (Date.now() - lastSaved.current > 5000) {
          API.put('/candidates/time', { timeRemaining: next }).catch(error => {
            if (error.response?.status !== 423) {
              console.error('Failed to save remaining assessment time:', error);
            }
          });
          lastSaved.current = Date.now();
        }
        if (next <= 0) {
          clearInterval(timerRef.current);
          setTimeExpired(true); // Instead of auto-submit, mark time as expired
          setSkipConfirmModal(true); // Skip confirmation modal when time expired
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [accessReady, assessmentPaused, accountDeactivated, accessError, submitted]);

  // Save answers to localStorage whenever they change
  useEffect(() => {
    if (candidate) {
      localStorage.setItem(`assessmentAnswers_${candidate.id}`, JSON.stringify(answers));
    }
  }, [answers, candidate]);

  // Save current question index to localStorage whenever it changes
  useEffect(() => {
    if (candidate) {
      localStorage.setItem(`assessmentCurrentQ_${candidate.id}`, JSON.stringify(currentQ));
    }
  }, [currentQ, candidate]);

  // Save time remaining to localStorage to persist across server restarts
  useEffect(() => {
    if (candidate && timeLeft >= 0) {
      localStorage.setItem(`assessmentTime_${candidate.id}`, timeLeft.toString());
    }
  }, [timeLeft, candidate]);

  // Camera drag handlers
  const handleCameraMouseDown = (e) => {
    setIsDragging(true);
    const rect = cameraCardRef.current.getBoundingClientRect();
    setDragOffset({
      x: window.innerWidth - e.clientX - rect.width,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setCameraPos({
      x: Math.max(0, window.innerWidth - e.clientX - dragOffset.x),
      y: Math.max(0, e.clientY - dragOffset.y)
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add/remove drag listeners
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const setAnswer = (section, qIdx, value) => {
    if (timeExpired || submitting || assessmentPaused || accountDeactivated || accessError || !accessReady) return;
    setAnswers(p => ({ ...p, [section]: { ...p[section], [qIdx]: value } }));
  };

  const handleSubmit = useCallback(async (auto = false) => {
    if (submitting || submitted || assessmentPaused || accountDeactivated || accessError || !accessReady) return;
    clearInterval(timerRef.current);
    setSubmitting(true);
    
    if (auto) {
      setAutoSubmitting(true);
    }

    try {
      let currentQuestions = questions;

      // If auto-submitting due to timeout, ensure ALL sections are loaded first
      if (auto) {
        const unloadedSections = SECTIONS.filter(({ key }) => !currentQuestions[key] || currentQuestions[key].length === 0);
        
        if (unloadedSections.length > 0) {
          console.log('Auto-submit: Loading unloaded sections before submission', unloadedSections.map(s => s.key));
          
          try {
            const loadedData = await Promise.all(
              unloadedSections.map(({ key }) =>
                API.get(`/questions/${key}`).then(res => ({ [key]: res.data }))
              )
            );
            
            // Merge loaded questions with current state
            currentQuestions = { ...currentQuestions };
            loadedData.forEach(loaded => {
              Object.assign(currentQuestions, loaded);
            });
            
            // Update state with loaded questions
            setQuestions(prev => ({ ...prev, ...loadedData.reduce((acc, loaded) => Object.assign(acc, loaded), {}) }));
          } catch (loadErr) {
            console.warn('Failed to load all sections before auto-submit, proceeding with available data', loadErr);
            // Continue with whatever sections are loaded - don't fail completely
          }
        }
      }

      const sectionsPayload = SECTIONS.map(({ key }) => {
        const qs = currentQuestions[key] || [];
        const ans = answers[key] || {};
        const answersArr = qs.map((_, idx) => ({ answer: ans[idx] ?? null }));
        return { section: key, answers: answersArr };
      });

      // Retry logic for auto-submit (timeout case)
      let submitError = null;
      let retries = auto ? 2 : 0; // 2 retries for auto-submit, 0 for manual
      
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          await API.post('/assessment/submit', { sections: sectionsPayload, autoSubmitted: auto });
          
          if (candidate) {
            localStorage.removeItem(`assessmentAnswers_${candidate.id}`);
            localStorage.removeItem(`assessmentCurrentQ_${candidate.id}`);
            localStorage.removeItem(`assessmentTime_${candidate.id}`);
          }
          setAutoSubmitting(false);
          setSubmitted(true);
          setSubmitting(false);
          return; // Success - exit function
        } catch (e) {
          submitError = e;
          if (attempt < retries) {
            console.warn(`Submit attempt ${attempt + 1} failed, retrying...`, e);
            // Wait before retry (exponential backoff)
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          }
        }
      }
      
      // All retries failed
      throw submitError;
    } catch (e) {
      console.error('Assessment submission failed:', e);
      setAutoSubmitting(false);
      setSubmitting(false);
      
      // Show error alert for timeout case
      if (auto) {
        alert('Your assessment time has expired. Your submission is being saved. If you do not see the success page, please contact the administrator.');
      }
    }
  }, [submitting, submitted, questions, answers, candidate, assessmentPaused, accountDeactivated, accessError, accessReady]);

  const handleSectionSubmit = (currentSectionKey) => {
    const qs = questions[currentSectionKey];
    const ans = answers[currentSectionKey];
    
    // Count unanswered questions (but don't block submission)
    let unansweredCount = 0;
    for (let i = 0; i < qs.length; i++) {
      if (ans[i] === undefined || (typeof ans[i] === 'string' && ans[i].trim() === '')) {
        unansweredCount++;
      }
    }
    
    // Show confirmation modal with unanswered count info
    setSectionSubmitInfo({
      section: currentSectionKey,
      unansweredCount
    });
    setSectionConfirmModal(currentSectionKey);
  };

  const handleConfirmSectionSubmit = () => {
    const currentSectionKey = sectionConfirmModal;
    const currentSectionIdx = SECTIONS.findIndex(s => s.key === currentSectionKey);
    setSectionConfirmModal(null);
    setSectionSubmitInfo(null);
    
    if (currentSectionIdx < SECTIONS.length - 1) {
      // Show transition screen for next section
      const nextSection = SECTIONS[currentSectionIdx + 1];
      setSectionTransition({ from: currentSectionKey, to: nextSection.key });
    } else {
      // Last section - trigger final submit
      handleFinalSubmitClick();
    }
  };

  const handleCancelSectionSubmit = () => {
    setSectionConfirmModal(null);
    setSectionSubmitInfo(null);
  };

  const handleFinalSubmitClick = () => {
    // Ensure all sections are loaded before checking
    const unloadedSections = SECTIONS.filter(({ key }) => questions[key].length === 0);
    
    if (unloadedSections.length > 0) {
      // Load all unloaded sections
      Promise.all(
        unloadedSections.map(({ key }) =>
          API.get(`/questions/${key}`).then(res => {
            setQuestions(p => ({ ...p, [key]: res.data }));
            return { [key]: res.data };
          })
        )
      ).then(loadedSections => {
        // Merge loaded questions with current state
        const allQuestions = { ...questions };
        loadedSections.forEach(loaded => {
          Object.assign(allQuestions, loaded);
        });
        
        // Now check for unanswered questions with all sections loaded
        checkAllSectionsAndShowWarning(allQuestions);
      }).catch(() => {
        // If loading fails, still attempt to submit with available data
        checkAllSectionsAndShowWarning(questions);
      });
    } else {
      // All sections already loaded, check for unanswered
      checkAllSectionsAndShowWarning(questions);
    }
  };

  const checkAllSectionsAndShowWarning = (allQuestions) => {
    // Check for unanswered questions across all sections
    const unansweredBySection = {};
    let totalUnanswered = 0;
    
    SECTIONS.forEach(({ key }) => {
      const qs = allQuestions[key];
      const ans = answers[key];
      let count = 0;
      
      for (let i = 0; i < qs.length; i++) {
        if (ans[i] === undefined || (typeof ans[i] === 'string' && ans[i].trim() === '')) {
          count++;
        }
      }
      
      if (count > 0) {
        unansweredBySection[key] = count;
        totalUnanswered += count;
      }
    });
    
    if (totalUnanswered > 0) {
      setFinalSubmitWarning({
        unansweredBySection,
        totalUnanswered
      });
    } else if (skipConfirmModal) {
      // Skip confirmation modal when time has expired
      handleSubmit(false);
    } else {
      setShowSubmitModal(true);
    }
  };

  const handleConfirmSubmit = () => {
    setShowSubmitModal(false);
    handleSubmit(false);
  };

  const handleCancelSubmit = () => {
    setShowSubmitModal(false);
  };

  const handleContinueToNextSection = () => {
    if (sectionTransition) {
      const nextSectionKey = sectionTransition.to;
      switchSection(nextSectionKey);
      // Case study starts at scenario (-1), other sections start at 0
      const startIdx = nextSectionKey === 'casestudy' ? -1 : 0;
      setCurrentQ(p => ({ ...p, [nextSectionKey]: startIdx }));
      setSectionTransition(null);
    }
  };

  const totalAnswered = () => {
    return Object.values(answers).reduce((total, secAns) => total + Object.keys(secAns).length, 0);
  };

  const totalQuestions = () => {
    return Object.values(questions).reduce((t, qs) => t + qs.length, 0);
  };

  const sectionAnswered = (sec) => Object.keys(answers[sec]).length;
  const sectionTotal = (sec) => questions[sec].length;
  const sectionComplete = (sec) => sectionAnswered(sec) === sectionTotal(sec) && sectionTotal(sec) > 0;

  const timerWarning = timeLeft < 600; // red when < 10 mins

  // Auto-submit loading screen (shown when time expires)
  if (autoSubmitting) {
    return (
      <div className={styles.submittedPage}>
        <div className={styles.submittedCard}>
          <div className={styles.submittedIcon}><FiClock aria-hidden="true" /></div>
          <h2 className={styles.submittedTitle}>Time's Up</h2>
          <p className={styles.submittedSub}>Your assessment time has elapsed. Submitting your responses...</p>
          <p className={styles.submittedNote}>Please wait while your answers are being saved to the system. Do not close this window.</p>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={styles.submittedPage}>
        <div className={styles.submittedCard}>
          <div className={styles.submittedIcon}><FiCheck aria-hidden="true" /></div>
          <h2 className={styles.submittedTitle}>Assessment Submitted</h2>
          <p className={styles.submittedSub}>Thank you, {candidate?.fullName?.split(' ')[0]}. Your responses have been recorded successfully.</p>
          <p className={styles.submittedNote}>Your results will be reviewed by the assessment administrator. You will be contacted with next steps.</p>
          <p className={styles.countdownText}>Signing out in <span className={styles.countdownNumber}>{countdownSeconds}</span> seconds...</p>
          <button className={styles.submittedBtn} onClick={() => { 
            if (candidate) {
              localStorage.removeItem(`assessmentSubmitted_${candidate.id}`);
            }
            candidateLogout(); 
            navigate('/login'); 
          }}>Sign Out</button>
        </div>
      </div>
    );
  }

  if (!accessReady || assessmentPaused || accountDeactivated || accessError) {
    const accessTitle = !accessReady
      ? 'Checking assessment access'
      : accessError
        ? 'Unable to verify assessment access'
        : accountDeactivated
          ? 'Candidate account deactivated'
          : 'Assessment paused';
    const accessMessage = !accessReady
      ? 'Please wait while we verify your assessment access.'
      : accessError
        ? 'We could not confirm your assessment access. Your assessment is locked while we retry. Please check your connection.'
        : accountDeactivated
          ? 'Your candidate account has been deactivated. Please contact the administrator if you have any questions.'
          : 'An administrator has paused your assessment. You cannot continue at this time. If you have any further enquiries, please contact the administrator. Your saved progress will be available when access is restored.';

    return (
      <div className={styles.accessBlockedPage}>
        <div className={styles.accessBlockedCard} role="dialog" aria-modal="true" aria-labelledby="assessment-access-title">
          <div className={styles.accessBlockedIcon}><FiAlertTriangle aria-hidden="true" /></div>
          <h2 id="assessment-access-title" className={styles.accessBlockedTitle}>{accessTitle}</h2>
          <p className={styles.accessBlockedMessage}>{accessMessage}</p>
          {(assessmentPaused || accountDeactivated || accessError) && (
            <p className={styles.accessBlockedWait}>This message will update automatically when your access status changes.</p>
          )}
        </div>
      </div>
    );
  }

  if (sectionTransition) {
    const nextSectionIdx = SECTIONS.findIndex(s => s.key === sectionTransition.to);
    const nextSection = SECTIONS[nextSectionIdx];
    const sectionDescriptions = {
      cognitive: 'Rate each question based on your best judgment. There are no right or wrong answers.',
      personality: 'Rate each statement on a scale of 1 (Strongly Disagree) to 5 (Strongly Agree). Choose the response that best reflects how you actually behave, not how you think you should behave. There are no right or wrong answers here.',
      leadership: 'Read each scenario and select the best response based on leadership principles. There are no right or wrong answers here.',
      casestudy: 'Provide detailed written responses to the case study questions. Focus on practical, realistic solutions.'
    };

    return (
      <div className={styles.transitionPage}>
        <div className={styles.transitionCard}>
          <div className={styles.transitionIcon}><FiArrowRightCircle aria-hidden="true" /></div>
          <h2 className={styles.transitionTitle}>Section {nextSectionIdx + 1} of {SECTIONS.length}</h2>
          <h3 className={styles.transitionSectionName}>{nextSection.label}</h3>
          <p className={styles.transitionDesc}>{sectionDescriptions[nextSection.key]}</p>
          <div className={styles.transitionMeta}>
            <p className={styles.transitionCount}>{questions[nextSection.key].length} statements to complete.</p>
            <p className={styles.transitionCompleted}>Previous sections completed <FiCheck aria-hidden="true" /></p>
          </div>
          <button className={styles.continueBtn} onClick={handleContinueToNextSection}>
            CONTINUE →
          </button>
        </div>
      </div>
    );
  }

  const qs = questions[activeSection];
  const qIdx = currentQ[activeSection];
  
  // For case study, treat scenario as a navigable card at index -1
  const showScenario = activeSection === 'casestudy' && qIdx === -1;
  const currentQuestion = showScenario ? null : qs[qIdx];

  return (
    <div className={styles.page}>
      {/* Top Bar */}
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <img src="/logo.png" alt="UAC Foods Logo" className={styles.logoMark} />
          <span className={styles.topBarTitle}>Sales Leadership Assessment</span>
        </div>
        <div className={styles.topBarCenter}>
          <div className={`${styles.timer} ${timerWarning ? styles.timerWarn : ''} ${timeExpired ? styles.timerExpired : ''}`}>
            <span className={styles.timerIcon}><FiClock aria-hidden="true" /></span>
            <span className={styles.timerVal}>{timeExpired ? 'Time Up, Click Submit' : formatTime(timeLeft)}</span>
          </div>
        </div>
        <div className={styles.topBarRight}>
          <span className={styles.userLabel}>{candidate?.fullName} • {candidate?.role}</span>
        </div>
      </div>

      {/* Time Expired Banner */}
      {timeExpired && (
        <div className={styles.timeExpiredBanner}>
          <div className={styles.timeExpiredContent}>
            <span className={styles.timeExpiredIcon}><FiAlertTriangle aria-hidden="true" /></span>
            <div className={styles.timeExpiredText}>
              <strong>Time's Up!</strong> Click the <strong>Submit Assessment</strong> button below to submit your answers.
            </div>
          </div>
        </div>
      )}

      {/* Camera Feed Card */}
      <div 
        ref={cameraCardRef}
        className={styles.cameraCard}
        style={{
          right: `${cameraPos.x}px`,
          top: `${cameraPos.y}px`,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleCameraMouseDown}
      >
        <div className={styles.cameraLabel}><FiCamera aria-hidden="true" /> You</div>
        <video 
          ref={videoRef} 
          className={styles.cameraVideo}
          autoPlay 
          muted 
          playsInline 
          draggable={false}
        />
      </div>

      {/* Progress bar */}
      <div className={styles.progressOuter}>
        <div className={styles.progressInner} style={{ width: `${totalQuestions() > 0 ? (totalAnswered() / totalQuestions()) * 100 : 0}%` }} />
      </div>

      {/* Section Tabs */}
      <div className={styles.tabs}>
        <div className={styles.tabsContainer}>
          {SECTIONS.map(({ key, label }) => (
            <button
              key={key}
              className={`${styles.tab} ${activeSection === key ? styles.tabActive : ''} ${sectionComplete(key) ? styles.tabDone : ''}`}
              onClick={() => !submitting && !timeExpired && switchSection(key)}
              disabled={submitting || timeExpired}
            >
              {sectionComplete(key) && <span className={styles.tabCheck}><FiCheckCircle aria-hidden="true" /></span>}
              <span>{label}</span>
              <span className={styles.tabCount}>{sectionAnswered(key)}/{sectionTotal(key) || '—'}</span>
            </button>
          ))}
        </div>
        <button
          className={styles.tabSubmitBtn}
          onClick={handleFinalSubmitClick}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Assessment'}
        </button>
      </div>

      {/* Question Area */}
      <div className={`${styles.main} ${timeExpired ? styles.mainBlurred : ''}`}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>{SECTIONS.find(s => s.key === activeSection)?.badgeLabel}</span>
          {qs.length > 0 && (
            <span className={styles.qCount}>
              {showScenario ? 'Scenario' : `Question ${qIdx + 1} of ${qs.length}`}
            </span>
          )}
        </div>

        {loadingQ ? (
          <QuestionSkeleton />
        ) : qs.length === 0 ? (
          <QuestionSkeleton />
        ) : (
          <>
            {/* Case Study Scenario Card */}
            {showScenario && activeSection === 'casestudy' && (
              <div className={styles.scenarioCardWrapper}>
                {/* Red Header Section */}
                <div className={styles.scenarioHeader}>
                  <h2 className={styles.scenarioHeaderTitle}>South West Territory — Performance Review</h2>
                  <p className={styles.scenarioHeaderSubtitle}>Q1 (January – March) vs. Q2 (April – June)</p>
                </div>

                {/* Body Section */}
                <div className={styles.scenarioBody}>
                  <p>You are reviewing the half-year performance for the South West territory. Study the data table and contextual notes carefully before answering the analytical questions that follow.</p>

                  {/* KPI Data Table */}
                  <table className={styles.kpiTable}>
                    <thead>
                      <tr>
                        <th>KPI</th>
                        <th>Q1 Target</th>
                        <th>Q1 Actual</th>
                        <th>Q1 Ach.</th>
                        <th>Q2 Target</th>
                        <th>Q2 Actual</th>
                        <th>Q2 Ach.</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Primary Sales (₦M)</td>
                        <td>250</td>
                        <td>220</td>
                        <td className={styles.percentOrange}>88%</td>
                        <td>275</td>
                        <td>198</td>
                        <td className={styles.percentRed}>72%</td>
                      </tr>
                      <tr>
                        <td>Secondary Sales (₦M)</td>
                        <td>220</td>
                        <td>195</td>
                        <td className={styles.percentOrange}>89%</td>
                        <td>245</td>
                        <td>165</td>
                        <td className={styles.percentRed}>67%</td>
                      </tr>
                      <tr>
                        <td>Call Completion (%)</td>
                        <td>90%</td>
                        <td>85%</td>
                        <td className={styles.percentOrange}>94%</td>
                        <td>90%</td>
                        <td>71%</td>
                        <td className={styles.percentRed}>79%</td>
                      </tr>
                      <tr>
                        <td>Drop Size (₦K avg)</td>
                        <td>45</td>
                        <td>42</td>
                        <td className={styles.percentOrange}>93%</td>
                        <td>50</td>
                        <td>35</td>
                        <td className={styles.percentRed}>70%</td>
                      </tr>
                      <tr>
                        <td>Strike Rate (%)</td>
                        <td>65%</td>
                        <td>62%</td>
                        <td className={styles.percentGreen}>95%</td>
                        <td>65%</td>
                        <td>55%</td>
                        <td className={styles.percentRed}>85%</td>
                      </tr>
                      <tr>
                        <td>Active Outlets</td>
                        <td>800</td>
                        <td>780</td>
                        <td className={styles.percentGreen}>97.5%</td>
                        <td>850</td>
                        <td>710</td>
                        <td className={styles.percentRed}>84%</td>
                      </tr>
                      <tr>
                        <td>DSO (days)</td>
                        <td>≤35</td>
                        <td>38</td>
                        <td className={styles.percentRed}>Breach</td>
                        <td>≤35</td>
                        <td>52</td>
                        <td className={styles.percentRed}>Breach</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Contextual Notes */}
                  <div className={styles.contextualNotes}>
                    <h3 className={styles.contextualNotesTitle}>Contextual Notes</h3>
                    <ul className={styles.contextualNotesList}>
                      <li>A new competitor brand launched in March with aggressive price positioning across key SKU categories.</li>
                      <li>Three experienced sales reps resigned in Q2. Replacements have not yet been hired.</li>
                      <li>A new distributor was onboarded in April to expand coverage but is still in the ramp-up phase.</li>
                      <li>Fuel price increases in Q2 raised distributor delivery and operating costs significantly.</li>
                      <li>One key account (contributing ₦25M/month) suspended orders following an unresolved debt dispute.</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Question Card */}
            {!showScenario && currentQuestion && (
              <div className={styles.questionCard} key={`${activeSection}-${qIdx}`}>
                <div className={styles.questionText}>
                  <span className={styles.qNum}>{qIdx + 1}.</span> {currentQuestion?.text}
                </div>

            {/* MCQ / Scenario Options */}
            {(currentQuestion?.type === 'mcq' || currentQuestion?.type === 'scenario') && (
              <div className={styles.options}>
                {currentQuestion.options.map(opt => (
                  <button
                    key={opt.label}
                    className={`${styles.option} ${answers[activeSection][qIdx] === opt.value ? styles.optionSelected : ''}`}
                    onClick={() => !submitting && !timeExpired && setAnswer(activeSection, qIdx, opt.value)}
                    disabled={submitting || timeExpired}
                  >
                    <span className={styles.optionLabel}>{opt.label}.</span>
                    <span className={styles.optionText}>{opt.text}</span>
                  </button>
                ))}
              </div>
            )}
            {(currentQuestion?.type === 'mcq' || currentQuestion?.type === 'scenario') && (
              <div className={styles.optionMsg}>
                {answers[activeSection][qIdx] === undefined ? 'Select an answer to continue' : ''}
              </div>
            )}

            {/* Likert Scale */}
            {currentQuestion?.type === 'likert' && (
              <div className={styles.likertWrap}>
                <div className={styles.likertScale}>
                  {[
                    { val: 1, label: 'Strongly Disagree' },
                    { val: 2, label: 'Disagree' },
                    { val: 3, label: 'Neutral' },
                    { val: 4, label: 'Agree' },
                    { val: 5, label: 'Strongly Agree' }
                  ].map(item => (
                    <div key={item.val} className={styles.likertOption}>
                      <span className={styles.likertLabel}>{item.label}</span>
                      <button
                        className={`${styles.likertBtn} ${answers[activeSection][qIdx] === item.val ? styles.likertSelected : ''}`}
                        onClick={() => !submitting && !timeExpired && setAnswer(activeSection, qIdx, item.val)}
                        disabled={submitting || timeExpired}
                      >
                        {item.val}
                      </button>
                    </div>
                  ))}
                </div>
                <div className={styles.likertMsg}>
                  {answers[activeSection][qIdx] === undefined ? 'Select an answer to continue' : ''}
                </div>
              </div>
            )}

            {/* Open Text */}
            {currentQuestion?.type === 'open' && (
              <div className={styles.openWrap}>
                <textarea
                  className={styles.textarea}
                  rows={8}
                  placeholder="Type your detailed response here..."
                  value={answers[activeSection][qIdx] || ''}
                  onChange={e => !submitting && !timeExpired && setAnswer(activeSection, qIdx, e.target.value)}
                  disabled={submitting || timeExpired}
                />
                <div className={styles.charCount}>{(answers[activeSection][qIdx] || '').length} characters</div>
                <div className={styles.openMsg}>
                  {answers[activeSection][qIdx] === undefined || answers[activeSection][qIdx]?.trim() === '' ? 'Enter your response to continue' : ''}
                </div>
              </div>
            )}
          </div>
            )}
          </>
        )}

        {/* Navigation */}
        {qs.length > 0 && (
          <>
            <div className={styles.navRow}>
              <button
                className={styles.navBtn}
                onClick={() => !submitting && !timeExpired && setCurrentQ(p => ({ ...p, [activeSection]: Math.max(activeSection === 'casestudy' ? -1 : 0, p[activeSection] - 1) }))}
                disabled={submitting || timeExpired || qIdx === (activeSection === 'casestudy' ? -1 : 0)}
              >
                ← Back
              </button>
              <div className={styles.navCenter}>
                {/* Scenario dot for case study */}
                {activeSection === 'casestudy' && (
                  <button
                    className={`${styles.navDot} ${styles.navDotScenario} ${qIdx === -1 ? styles.navDotActive : ''}`}
                    onClick={() => !submitting && !timeExpired && setCurrentQ(p => ({ ...p, [activeSection]: -1 }))}
                    disabled={submitting || timeExpired}
                    title="Case Study Scenario"
                  >
                    Scenario
                  </button>
                )}
                {/* Question dots */}
                {qs.map((_, i) => (
                  <button
                    key={i}
                    className={`${styles.navDot} ${i === qIdx ? styles.navDotActive : ''} ${answers[activeSection][i] !== undefined ? styles.navDotAnswered : ''}`}
                    onClick={() => !submitting && !timeExpired && setCurrentQ(p => ({ ...p, [activeSection]: i }))}
                    disabled={submitting || timeExpired}
                    title={`Question ${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                className={styles.navBtnPrimary}
                onClick={() => {
                  if (submitting || timeExpired) return;
                  const nextIdx = qIdx + 1;
                  const maxIdx = qs.length - 1;
                  if (nextIdx <= maxIdx) {
                    setCurrentQ(p => ({ ...p, [activeSection]: nextIdx }));
                  }
                }}
                disabled={submitting || timeExpired || qIdx >= qs.length - 1}
              >
                Next →
              </button>
            </div>

            {/* Submit Actions Row */}
            <div className={styles.actionsRow}>
              <button
                className={styles.actionBtn}
                onClick={() => handleSectionSubmit(activeSection)}
                disabled={submitting || timeExpired}
              >
                {submitting ? 'Processing...' : 'Submit Section →'}
              </button>
            </div>
          </>
        )}

        {/* Submit Area - removed */}
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalCard}>
            <h2 className={styles.modalTitle}>Submit Assessment?</h2>
            <p className={styles.modalMessage}>You have completed all sections. Are you sure you want to submit your assessment? This cannot be undone.</p>
            <div className={styles.modalActions}>
              <button className={styles.modalBtnCancel} onClick={handleCancelSubmit} disabled={submitting}>
                Cancel
              </button>
              <button className={styles.modalBtnSubmit} onClick={handleConfirmSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Assessment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Confirmation Modal */}
      {sectionConfirmModal && (() => {
        const isLastSection = SECTIONS.findIndex(s => s.key === sectionConfirmModal) === SECTIONS.length - 1;
        return (
          <div className={styles.modalOverlay}>
            <div className={styles.modalCard}>
              <h2 className={styles.modalTitle}>
                {sectionSubmitInfo?.unansweredCount > 0 && <FiAlertTriangle aria-hidden="true" />}
                {sectionSubmitInfo?.unansweredCount > 0 ? 'Unanswered Questions' : (isLastSection ? 'Last Section Complete?' : 'Section Complete?')}
              </h2>
              {sectionSubmitInfo?.unansweredCount > 0 ? (
                <>
                  <p className={styles.modalMessage}>
                    You have {sectionSubmitInfo.unansweredCount} unanswered question{sectionSubmitInfo.unansweredCount !== 1 ? 's' : ''} in this section. 
                    Do you still want to {isLastSection ? 'complete the assessment?' : 'proceed to the next section?'}
                  </p>
                  <div className={styles.warningList} style={{ marginTop: '1rem' }}>
                    <div className={styles.warningItem}>
                      Unanswered: <strong>{sectionSubmitInfo.unansweredCount}</strong>
                    </div>
                  </div>
                </>
              ) : (
                <p className={styles.modalMessage}>
                  You have answered all questions in this section. 
                  {isLastSection ? ' You can now submit your assessment.' : ' Are you sure you want to proceed to the next section?'}
                </p>
              )}
              <div className={styles.modalActions}>
                <button className={styles.modalBtnCancel} onClick={handleCancelSectionSubmit}>
                  Review Answers
                </button>
                <button className={styles.modalBtnSubmit} onClick={handleConfirmSectionSubmit}>
                  {isLastSection ? (sectionSubmitInfo?.unansweredCount > 0 ? 'Complete Anyway →' : 'Submit Assessment') : (sectionSubmitInfo?.unansweredCount > 0 ? 'Proceed Anyway →' : 'Proceed to Next Section →')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Validation Error Modal */}
      {validationError && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalCard}>
            <h2 className={styles.modalTitle} style={{ color: 'var(--red)' }}><FiAlertTriangle aria-hidden="true" /> {validationError.title}</h2>
            <p className={styles.modalMessage}>{validationError.message}</p>
            <div className={styles.modalActions}>
              <button 
                className={styles.modalBtnSubmit} 
                onClick={() => setValidationError(null)}
              >
                Got it, let me answer the questions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final Submit Warning Modal */}
      {finalSubmitWarning && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalCard}>
            <h2 className={styles.modalTitle} style={{ color: 'var(--red)' }}><FiAlertTriangle aria-hidden="true" /> Unanswered Questions</h2>
            <p className={styles.modalMessage}>You have {finalSubmitWarning.totalUnanswered} unanswered question{finalSubmitWarning.totalUnanswered !== 1 ? 's' : ''} across the following sections:</p>
            <div className={styles.warningList}>
              {Object.entries(finalSubmitWarning.unansweredBySection).map(([section, count]) => {
                const sectionLabel = SECTIONS.find(s => s.key === section)?.label || section;
                return (
                  <div key={section} className={styles.warningItem}>
                    <span>{sectionLabel}:</span>
                    <strong>{count} question{count !== 1 ? 's' : ''}</strong>
                  </div>
                );
              })}
            </div>
            <div className={styles.modalActions}>
              <button 
                className={styles.modalBtnCancel}
                onClick={() => setFinalSubmitWarning(null)}
              >
                Continue Answering
              </button>
              <button 
                className={styles.modalBtnSubmit}
                onClick={() => {
                  setFinalSubmitWarning(null);
                  setShowSubmitModal(true);
                }}
              >
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submission In Progress Overlay */}
      {submitting && (
        <div className={styles.submissionOverlay}>
          <div className={styles.submissionSpinner}>
            <div className={styles.spinner}></div>
            <p>Submitting your assessment...</p>
          </div>
        </div>
      )}
    </div>
  );
}
